const elapsedTimeFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 4,
})

const formatPegaTimestamp = (date: Date) => {
  const parts = Intl.DateTimeFormat('utc', {
    timeZone: 'UTC',
    hourCycle: 'h24',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    fractionalSecondDigits: 3,
  }).formatToParts(date)
  let [year, month, day, hour, minute, second, fraction] = ''
  for (const part of parts) {
    switch (part.type) {
      case 'year':
        year = part.value
        break
      case 'month':
        month = part.value
        break
      case 'day':
        day = part.value
        break
      case 'hour':
        hour = part.value
        break
      case 'minute':
        minute = part.value
        break
      case 'second':
        second = part.value
        break
      case 'fractionalSecond':
        fraction = part.value
        break
    }
  }

  return `${year}${month}${day}T${hour}${minute}${second}.${fraction} GMT`
}

export default class TraceEvent {
  key?: number
  traceLogKey?: number
  activePALStat?: string
  activityName?: string
  activityNumber?: number
  aDPLoadPageName?: string
  correlationId?: string
  dateTime?: Date
  elapsed?: number
  eventKey?: string
  eventName?: string
  eventType?: string
  firstInput?: string
  insKey?: string //
  interaction?: number
  interactionBytes?: number
  interactionQueryData?: string
  interactionQueryParam?: string
  javaStackTrace?: string //
  keyName?: string
  lastStep?: string
  localVariables: Map<string, string> = new Map()
  name?: string //
  namedPages: Map<string, string> = new Map()
  nodeId?: string
  nodeName?: string
  parameterPageContent?: Map<string, string> = new Map()
  parameterPageName?: string
  primaryPageClass?: string
  primaryPageContent?: string
  primaryPageName?: string
  requestorId?: string
  ruleNumber?: number //
  rulesetName?: string
  rulesetVersion?: string
  sequence?: number
  stepMethod?: string
  stepNumber?: string
  stepStatus?: string
  threadName?: string
  timestamp?: string //
  whenStatus?: string
  workPool?: string

  get sequenceDisplay() {
    return (this.sequence || 0) + 1
  }

  get nameDisplay(): string {
    const nameParts = this.name?.split(' ')
    switch (this.eventType) {
      case 'Interaction':
        return this.interactionQueryParam || ''
      case 'ADP Load':
        if (nameParts && nameParts.length > 4) {
          const parser = new DOMParser()
          const doc = parser.parseFromString(nameParts[4], 'text/html')
          return `${nameParts[1]} ${doc.body.textContent || ''}`
        }

        return ''
      default:
        return this.keyName || ''
    }
  }

  get rulesetDisplay(): string {
    switch (this.eventType) {
      case 'Access Denied':
      case 'DB Cache':
      case 'DB Query':
      case 'Interaction':
        return ''
      default:
        return `${this.rulesetName || ''} ${this.rulesetVersion || ''}`
    }
  }

  get statusDisplay(): string {
    switch (this.eventType) {
      case 'Interaction':
        return `${Intl.NumberFormat().format(this.interactionBytes || 0)}(b)`
      case 'When End':
        return this.whenStatus || ''
      default:
        return this.stepStatus || ''
    }
  }

  get elapsedDisplay(): string {
    if (this.elapsed) {
      return elapsedTimeFormatter.format(this.elapsed / 1000)
    }
    return ''
  }

  get dateTimeDisplay(): string {
    if (this.dateTime) {
      return `${Intl.DateTimeFormat(navigator.language, { dateStyle: 'medium', timeStyle: 'long' }).format(this.dateTime)} (${formatPegaTimestamp(this.dateTime)})`
    }
    return ''
  }

  get stepMethodDisplay(): string {
    switch (this.eventType) {
      case 'ADP Load':
        return this.eventName || ''
      default:
        return this.stepMethod || ''
    }
  }

  get primaryPageNameDisplay(): string {
    switch (this.eventType) {
      case 'ADP Load':
        return this.aDPLoadPageName || ''
      default:
        return this.primaryPageName || ''
    }
  }

  get eventNameDisplay(): string {
    switch (this.eventType) {
      case 'ADP Load':
        return this.eventType
      default:
        return this.eventName || ''
    }
  }

  public serialize(): object {
    const obj: { [key: string]: unknown } = {}
    Object.keys(this).forEach((key) => {
      const val = this[key as keyof TraceEvent]
      if (val !== undefined) {
        obj[key] = val
      }
    })

    return obj
  }
}
